import apiClient from './apiClient';

// User related API calls
export const userAPI = {
  fetchUsers: async (searchTerm, domain, duration, sendStatus = 'ALL') => {
    console.log('Fetching users with params:', { searchTerm, domain, duration, sendStatus });
    const response = await apiClient.post('/api/user/getUsers', {
      searchTerm,
      domain,
      duration,
      sendStatus
    });
    return response.data;
  },

  updateUser: async (userData) => {
    const response = await apiClient.post('/api/user/update', userData);
    return response.data;
  },

  uploadCsvData: async (csvData) => {
    const response = await apiClient.post('/api/user/upload', csvData);
    return response.data;
  },

  getUserCounts: async () => {
    const response = await apiClient.post('/api/user/counts');
    return response.data;
  },
};

// Certificate Users related API calls
export const certificateUserAPI = {
  // Fetch certificate users with optional filters
  fetchAll: async (searchTerm = '', domain = '', duration = '', sendStatus = 'ALL') => {
    const response = await apiClient.get('/api/cert-users', {
      params: { searchTerm, domain, duration, sendStatus },
    });
    return response.data;
  },

  // Create certificate user
  create: async (payload) => {
    const response = await apiClient.post('/api/cert-users', payload);
    return response.data;
  },

  // Update certificate user by id
  update: async (id, updates) => {
    const response = await apiClient.put(`/api/cert-users/${id}`, updates);
    return response.data;
  },

  // Delete certificate user by id
  remove: async (id) => {
    const response = await apiClient.delete(`/api/cert-users/${id}`);
    return response.data;
  },

  // Upload CSV where client parses CSV and sends JSON array
  uploadCsvJson: async (records) => {
    const response = await apiClient.post('/api/cert-users/csv/upload-json', records);
    return response.data;
  },

  // Upload CSV file (FormData with field name 'file')
  uploadCsvFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/api/cert-users/csv/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Download headers-only template CSV
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/cert-users/csv/template', { responseType: 'blob' });
    return response.data;
  },

  // Send certificate PNG via email for a certificate user
  sendCertificateEmail: async (userId) => {
    const response = await apiClient.post('/api/cert-users/sendCertificate', { userId });
    return response.data;
  },

  // Dashboard counts for certificate users
  getCounts: async () => {
    const response = await apiClient.post('/api/cert-users/counts');
    return response.data;
  },
};

// Document generation API calls
export const documentAPI = {
  generateOfferLetter: async (userId) => {
    const response = await apiClient.post(
      '/api/user/generateSingleOfferLetter',
      { userId },
      { responseType: 'blob' }
    );
    return response.data;
  },

  generateCertificate: async (userId) => {
    const response = await apiClient.post('/api/user/generateCertificate', { userId });
    return response.data;
  },

  downloadCertificate: async (userId) => {
    const response = await apiClient.post(
      '/api/user/downloadgenerateCertificate',
      { userId },
      { responseType: 'blob' }
    );
    return response.data;
  },

  generateInternshipPdf: async (userId) => {
    const response = await apiClient.post(
      '/api/user/generateInternshipPdf',
      { userId },
      { responseType: 'blob' }
    );
    return response;
  },

  sendOfferLetterByEmail: async (userId) => {
    const response = await apiClient.post('/api/user/sendOfferLetterByEmail', { userId });
    return response.data;
  },
};

// Admin related API calls
export const adminAPI = {
  create: async (payload) => {
    const response = await apiClient.post('/admin/create', payload);
    return response.data;
  },

  login: async (credentials) => {
    try {
      const response = await apiClient.post('/admin/login', credentials);
      
      if (response.data.token) {
        localStorage.setItem('adminToken', response.data.token);
        return response.data.token;
      }
      throw new Error('Token not found in response');
    } catch (error) {
      console.error('Login error:', error);
      throw error.response?.data || { message: 'Network error' };
    }
  },
};

// Email Config related API calls
export const emailConfigAPI = {
  getAll: async () => {
    const response = await apiClient.get('/api/email-config/getAll');
    return response.data;
  },

  // Get active email configuration
  getActive: async () => {
    const response = await apiClient.get('/api/email-config/getActive');
    return response.data;
  },

  // Get a specific email config by id
  getById: async (id) => {
    const response = await apiClient.get(`/api/email-config/get/${id}`);
    return response.data;
  },

  // Create a new email config
  create: async (payload) => {
    const response = await apiClient.post('/api/email-config/create', payload);
    return response.data;
  },

  // Update existing email config
  update: async (id, updates) => {
    const response = await apiClient.put(`/api/email-config/update/${id}`, updates);
    return response.data;
  },

  // Delete email config
  remove: async (id) => {
    const response = await apiClient.delete(`/api/email-config/delete/${id}`);
    return response.data;
  },
};